// ── useMeetingMapSettings — 미팅지도 설정 모달 상태 & API ─────────────────────
// 피드백 옵션 CRUD·미팅인계 담당자 토글·배너 문구. 동적관리(useTicketManagement/
// usePersonsAndWorkflow/OperationsSettingsPanels)에서 발췌 이식 (2026-09-01 설정 이관).
import { useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { inferFeedbackToneKey } from '../../../components/meetingmap/tones';

export function useMeetingMapSettings() {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  // ── 피드백 옵션 ────────────────────────────────────────────────────────────
  const [feedbackOptions,     setFeedbackOptions]     = useState([]);
  const [feedbackLoading,     setFeedbackLoading]     = useState(false);
  const [newFeedbackName,     setNewFeedbackName]     = useState('');
  const [newFeedbackColorKey, setNewFeedbackColorKey] = useState('blue');
  const [editFbId,            setEditFbId]            = useState(null);
  const [editFbName,          setEditFbName]          = useState('');
  const [editFbColorKey,      setEditFbColorKey]      = useState('blue');
  const [confirmFbId,         setConfirmFbId]         = useState(null);

  // ── 미팅인계 담당자 ────────────────────────────────────────────────────────
  const [inheritPersons, setInheritPersons] = useState([]);
  const [inheritLoading, setInheritLoading] = useState(false);

  // ── 배너 문구 (기본값은 서버 ticketType.banner.js 와 동일) ──────────────────
  const [bannerPrefix,  setBannerPrefix]  = useState('오늘 미팅은');
  const [bannerSuffix,  setBannerSuffix]  = useState('오늘도 화이팅! 💪');
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerSaving,  setBannerSaving]  = useState(false);

  // ── 피드백 옵션 API ─────────────────────────────────────────────────────────
  async function fetchFeedbackOptions() {
    setFeedbackLoading(true);
    const r = await apiClient.get('/api/admin/feedback-options');
    setFeedbackLoading(false);
    if (r.ok) setFeedbackOptions(Array.isArray(r.data?.data) ? r.data.data : []);
    else showToast('피드백 옵션을 불러오지 못했습니다.', 'error');
  }

  async function addFeedback() {
    const name = newFeedbackName.trim();
    if (!name) return;
    const color_key = newFeedbackColorKey || inferFeedbackToneKey(name);
    setSaving(true);
    const r = await apiClient.post('/api/admin/feedback-options', { name, color_key });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '추가 실패', 'error'); return; }
    setNewFeedbackName('');
    setNewFeedbackColorKey('blue');
    fetchFeedbackOptions();
  }

  async function saveFeedbackEdit() {
    const name = editFbName.trim();
    if (!name) { showToast('옵션명을 입력해 주세요.', 'error'); return; }
    setSaving(true);
    const r = await apiClient.put(`/api/admin/feedback-options/${editFbId}`, { name, color_key: editFbColorKey });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '수정 실패', 'error'); return; }
    setEditFbId(null);
    fetchFeedbackOptions();
  }

  async function deleteFeedback(id) {
    setSaving(true);
    const r = await apiClient.del(`/api/admin/feedback-options/${id}`);
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '삭제 실패', 'error'); return; }
    setConfirmFbId(null);
    fetchFeedbackOptions();
  }

  // 피드백 옵션 자동킵 여부 전환 (일반 ↔ 자동킵 적용)
  async function toggleAutoKeep(id, currentAutoKeep) {
    setSaving(true);
    const r = await apiClient.patch(`/api/admin/feedback-options/${id}/auto-keep`, { auto_keep: !currentAutoKeep });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    fetchFeedbackOptions();
  }

  // ── 미팅인계 담당자 API ──────────────────────────────────────────────────────
  async function fetchInheritPersons() {
    setInheritLoading(true);
    const r = await apiClient.get('/api/admin/meeting-inherit/sales-persons');
    setInheritLoading(false);
    if (r.ok) setInheritPersons(Array.isArray(r.data?.data) ? r.data.data : []);
    else showToast('담당자 목록을 불러오지 못했습니다.', 'error');
  }

  // 낙관적 업데이트: 클릭 즉시 반영, 실패 시 롤백
  async function toggleInheritPerson(person) {
    const next = !person.is_eligible;
    setInheritPersons((prev) => prev.map((p) => p.id === person.id ? { ...p, is_eligible: next } : p));
    const r = await apiClient.put(`/api/admin/meeting-inherit/sales-persons/${person.id}`, { eligible: next });
    if (!r.ok) {
      setInheritPersons((prev) => prev.map((p) => p.id === person.id ? { ...p, is_eligible: !next } : p));
      showToast(r.data?.message || '변경 실패', 'error');
    }
  }

  // ── 배너 문구 API ───────────────────────────────────────────────────────────
  async function fetchBanner() {
    setBannerLoading(true);
    const r = await apiClient.get('/api/tm/meeting-banner');
    setBannerLoading(false);
    if (r.ok && r.data?.success) {
      setBannerPrefix(r.data.prefix ?? '오늘 미팅은');
      setBannerSuffix(r.data.suffix ?? '오늘도 화이팅! 💪');
    }
  }

  async function saveBanner() {
    setBannerSaving(true);
    const r = await apiClient.put('/api/admin/meeting-banner', { prefix: bannerPrefix, suffix: bannerSuffix });
    setBannerSaving(false);
    if (r.ok && r.data?.success) showToast('저장되었습니다.', 'success');
    else showToast(r.data?.message || '저장 실패', 'error');
  }

  return {
    saving,
    // 피드백 옵션
    feedbackOptions, feedbackLoading,
    newFeedbackName, setNewFeedbackName, newFeedbackColorKey, setNewFeedbackColorKey,
    editFbId, setEditFbId, editFbName, setEditFbName, editFbColorKey, setEditFbColorKey,
    confirmFbId, setConfirmFbId,
    fetchFeedbackOptions, addFeedback, saveFeedbackEdit, deleteFeedback, toggleAutoKeep,
    // 미팅인계 담당자
    inheritPersons, inheritLoading, fetchInheritPersons, toggleInheritPerson,
    // 배너 문구
    bannerPrefix, setBannerPrefix, bannerSuffix, setBannerSuffix,
    bannerLoading, bannerSaving, fetchBanner, saveBanner,
  };
}
